import React, { useEffect, useState } from 'react';
import {
  RefreshCw, Download, RotateCcw, ChevronLeft, ChevronRight, AlertCircle, FileEdit, FileText, FileSpreadsheet,
  Mail, MailCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import { mensajeErrorApi } from '../apiErrors';

const ESTADOS = [
  { value: '', label: 'Todos los estados' },
  { value: 'generada', label: 'Generada' },
  { value: 'firmada', label: 'Firmada' },
  { value: 'enviada', label: 'Enviada al SRI' },
  { value: 'autorizada', label: 'Autorizada' },
  { value: 'rechazada', label: 'Rechazada' },
  { value: 'anulada', label: 'Anulada' },
];

const ESTADO_STYLE = {
  generada: { background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' },
  firmada: { background: '#fff3e0', color: '#e65100' },
  enviada: { background: '#fff9c4', color: '#f57f17' },
  autorizada: { background: '#e8f5e9', color: '#2e7d32' },
  rechazada: { background: '#ffebee', color: '#c62828' },
  anulada: { background: '#f5f5f5', color: '#616161' },
};

const TIPO_LABEL = { servicios: 'Servicios', reembolsos: 'Reembolsos' };

const ESTADO_LABEL = {
  generada: 'Generada',
  firmada: 'Firmada',
  enviada: 'Enviada',
  autorizada: 'Autorizada',
  rechazada: 'Rechazada',
  anulada: 'Anulada',
};

function EstadoFacturaBadge({ estado }) {
  const style = ESTADO_STYLE[estado] || ESTADO_STYLE.anulada;
  return (
    <span style={{
      display: 'inline-block', padding: '1px 8px', borderRadius: '10px',
      fontSize: '11px', fontWeight: 600, ...style,
    }}>
      {ESTADO_LABEL[estado] || estado}
    </span>
  );
}

function formatFechaEmision(iso) {
  if (!iso) return '—';
  const [a, m, d] = iso.split('-');
  return `${d}/${m}/${a}`;
}

const dinero = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(2));

function truncarClave(clave) {
  if (!clave) return '—';
  return `${clave.slice(0, 8)}…${clave.slice(-6)}`;
}

const ESTADO_DESCRIPCION = {
  generada: {
    titulo: 'XML generado, pendiente de firma',
    instruccion: 'La factura fue generada pero no se pudo firmar. Presiona "Reintentar" para volver a intentar el proceso completo.',
  },
  firmada: {
    titulo: 'Firmada, pendiente de envío al SRI',
    instruccion: 'La factura fue firmada correctamente pero no se pudo enviar al SRI (el servicio pudo estar caído). Presiona "Reintentar" para enviarla.',
  },
  enviada: {
    titulo: 'Enviada al SRI, pendiente de autorización',
    instruccion: 'La factura fue enviada pero no se obtuvo la autorización (el SRI pudo estar lento o caído). Presiona "Reintentar" para consultar la autorización.',
  },
  rechazada: {
    titulo: 'Rechazada por el SRI',
    instruccion: 'El SRI rechazó la factura. Revisa los errores abajo. Si es un error temporal del SRI, presiona "Reintentar" (reenvía el mismo comprobante). Si es un error de datos (ej. dirección muy larga), corrige la información del cliente y presiona "Volver a emitir" (genera un comprobante nuevo con los datos corregidos).',
  },
  autorizada: { titulo: 'Autorizada por el SRI', instruccion: '' },
  anulada: { titulo: 'Anulada', instruccion: '' },
};

function interpretarError(mensaje) {
  if (!mensaje) return mensaje;
  const m = mensaje.toLowerCase();
  if (m.includes('persistenceexception') || m.includes('jdbcexception') || m.includes('could not execute statement'))
    return 'El servidor del SRI tiene un problema interno en su base de datos. Esto es temporal — intenta de nuevo en unos minutos.';
  if (m.includes('connection') || m.includes('timeout') || m.includes('no disponible'))
    return 'No se pudo conectar con el SRI. El servicio puede estar caído o lento. Intenta de nuevo más tarde.';
  if (m.includes('clave acceso registrada'))
    return 'Esta clave de acceso ya fue registrada en el SRI. La factura posiblemente ya fue autorizada — presiona "Reintentar" para consultar su estado.';
  return mensaje;
}

// Guarda en disco la respuesta binaria (blob) de una descarga.
function guardarArchivo(res, nombre) {
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', nombre);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function nombreDeLaRespuesta(res, defecto) {
  const m = /filename="([^"]+)"/.exec(res.headers['content-disposition'] || '');
  return m ? m[1] : defecto;
}

export default function FacturasPanel() {
  const [facturas, setFacturas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(20);
  const [reenviando, setReenviando] = useState(null);
  const [reemitiendo, setReemitiendo] = useState(null);
  const [erroresVisible, setErroresVisible] = useState(null);
  const [exportando, setExportando] = useState(null);
  const [enviandoCorreo, setEnviandoCorreo] = useState(null);

  const filtros = () => {
    const params = {};
    if (filtroEstado) params.estado = filtroEstado;
    if (desde) params.desde = desde;
    if (hasta) params.hasta = hasta;
    return params;
  };

  const cargar = () => {
    setCargando(true);
    api.get('/facturas-electronicas/', { params: filtros() })
      .then(res => setFacturas(res.data.results || res.data))
      .catch((err) => toast.error(mensajeErrorApi(err, 'No se pudieron cargar las facturas.')))
      .finally(() => setCargando(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(cargar, [filtroEstado, desde, hasta]);
  useEffect(() => setPagina(1), [filtroEstado, desde, hasta, porPagina]);

  const totalPaginas = Math.max(1, Math.ceil(facturas.length / porPagina));
  const paginaActual = Math.min(pagina, totalPaginas);
  const facturasPaginadas = facturas.slice((paginaActual - 1) * porPagina, paginaActual * porPagina);

  // Solo las autorizadas cuentan para la declaración.
  const autorizadas = facturas.filter((f) => f.estado === 'autorizada');
  const suma = (campo) => autorizadas.reduce((acc, f) => acc + Number(f[campo] || 0), 0);

  const puedeReenviar = (estado) => ['firmada', 'enviada', 'rechazada'].includes(estado);

  const reenviar = async (factura) => {
    setReenviando(factura.id);
    try {
      const res = await api.post(`/facturas-electronicas/${factura.id}/reenviar/`);
      if (res.data.estado === 'autorizada') {
        toast.success(`Factura ${factura.secuencial} autorizada por el SRI.`);
      } else {
        toast.success(`Factura reenviada. Estado: ${res.data.estado}`);
      }
      cargar();
    } catch (err) {
      const data = err.response?.data;
      toast.error(data?.error || mensajeErrorApi(err, 'Error al reenviar la factura.'));
    } finally {
      setReenviando(null);
    }
  };

  const volverAEmitir = async (factura) => {
    if (!window.confirm(
      `¿Volver a emitir la factura de ${TIPO_LABEL[factura.tipo] || factura.tipo}? ` +
      'Se generará un comprobante nuevo con los datos actuales del cliente. ' +
      'Corrige antes cualquier dato erróneo (ej. dirección) en el perfil del cliente.'
    )) return;
    setReemitiendo(factura.id);
    try {
      const res = await api.post(`/facturas-electronicas/${factura.id}/volver_a_emitir/`);
      if (res.data.estado === 'autorizada') {
        toast.success(`Factura nueva ${res.data.secuencial} autorizada por el SRI.`);
      } else {
        toast.success(`Factura nueva generada. Estado: ${res.data.estado}`);
      }
      cargar();
    } catch (err) {
      const data = err.response?.data;
      const msg = data?.error || mensajeErrorApi(err, 'Error al volver a emitir la factura.');
      const errores = data?.errores_sri || [];
      if (errores.length > 0) {
        toast.error(`${msg}\n${errores.map((e) => e.mensaje || e.informacionAdicional).join('; ')}`, { duration: 8000 });
      } else {
        toast.error(msg);
      }
    } finally {
      setReemitiendo(null);
    }
  };

  const descargar = async (factura, ruta, nombre, mensajeError) => {
    try {
      const res = await api.get(`/facturas-electronicas/${factura.id}/${ruta}/`, { responseType: 'blob' });
      guardarArchivo(res, nombre);
    } catch {
      toast.error(mensajeError);
    }
  };

  const numero = (f) => `${f.establecimiento}-${f.punto_emision}-${f.secuencial}`;
  const descargarXml = (f) => descargar(f, 'xml_autorizado', `factura_${f.clave_acceso}.xml`, 'No se pudo descargar el XML.');
  const descargarPdf = (f) => descargar(f, 'ride', `factura_${numero(f)}.pdf`, 'No se pudo generar el PDF.');

  // Envía el PDF y el XML al cliente. El correo se puede cambiar en el momento
  // (por ejemplo, a la dirección de su contador).
  const enviarAlCliente = async (f) => {
    const yaEnviada = f.correo_enviado_en
      ? ` OJO: ya se envió a ${f.correo_enviado_a} el ${new Date(f.correo_enviado_en).toLocaleDateString('es-EC')}.`
      : '';
    const destinatario = window.prompt(
      `Enviar la factura ${numero(f)} (PDF y XML) a este correo:${yaEnviada}`,
      f.cliente_correo || '',
    );
    if (destinatario === null) return;
    setEnviandoCorreo(f.id);
    try {
      const res = await api.post(`/facturas-electronicas/${f.id}/enviar/`, { destinatario: destinatario.trim() });
      setFacturas((prev) => prev.map((x) => (x.id === f.id ? res.data : x)));
      toast.success(`Factura enviada a ${res.data.correo_enviado_a}.`);
    } catch (err) {
      toast.error(err.response?.data?.error || mensajeErrorApi(err, 'No se pudo enviar la factura.'));
    } finally {
      setEnviandoCorreo(null);
    }
  };

  const exportar = async (formato) => {
    setExportando(formato);
    try {
      const res = await api.get('/facturas-electronicas/exportar/', {
        params: { ...filtros(), formato }, responseType: 'blob',
      });
      guardarArchivo(res, nombreDeLaRespuesta(res, `facturas.${formato}`));
    } catch (err) {
      toast.error(mensajeErrorApi(err, 'No se pudo generar el reporte.'));
    } finally {
      setExportando(null);
    }
  };

  return (
    <>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 'var(--spacing-3)', gap: 'var(--spacing-3)', flexWrap: 'wrap',
      }}>
        <h2 style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>Facturas Electrónicas</h2>
        <div style={{ display: 'flex', gap: 'var(--spacing-3)', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            style={{ minWidth: '160px', width: 'auto' }}
          >
            {ESTADOS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 0, whiteSpace: 'nowrap' }} className="text-sm">
            Desde
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} style={{ width: 'auto' }} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 0, whiteSpace: 'nowrap' }} className="text-sm">
            Hasta
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} style={{ width: 'auto' }} />
          </label>
          <button className="btn-secondary" onClick={cargar} style={{ whiteSpace: 'nowrap' }}>
            <RefreshCw size={14} /> Actualizar
          </button>
          <button className="btn-secondary" onClick={() => exportar('xlsx')} disabled={exportando !== null} style={{ whiteSpace: 'nowrap' }}>
            <FileSpreadsheet size={14} /> {exportando === 'xlsx' ? 'Generando...' : 'Excel'}
          </button>
          <button className="btn-secondary" onClick={() => exportar('csv')} disabled={exportando !== null} style={{ whiteSpace: 'nowrap' }}>
            <Download size={14} /> {exportando === 'csv' ? 'Generando...' : 'CSV'}
          </button>
        </div>
      </div>

      {!cargando && facturas.length > 0 && (
        <p className="text-sm text-secondary" style={{ marginBottom: 'var(--spacing-3)' }}>
          <strong>{autorizadas.length}</strong> autorizadas en este listado ·
          Subtotal <strong className="mono">${suma('subtotal').toFixed(2)}</strong> ·
          IVA <strong className="mono">${suma('iva').toFixed(2)}</strong> ·
          Total <strong className="mono">${suma('total').toFixed(2)}</strong>
          {' '}(las rechazadas o pendientes no se suman). El Excel / CSV usa los mismos filtros.
        </p>
      )}

      <div className="table-wrapper">
        <table className="tabla-compacta">
          <thead>
            <tr>
              <th>Emisión</th>
              <th>Factura</th>
              <th>Tipo</th>
              <th>Cliente</th>
              <th className="num">Subtotal</th>
              <th className="num">IVA</th>
              <th className="num">Total</th>
              <th>Estado</th>
              <th>Errores</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {cargando ? (
              <tr><td colSpan={10} className="text-secondary">Cargando...</td></tr>
            ) : facturas.length === 0 ? (
              <tr><td colSpan={10} className="text-secondary">No hay facturas electrónicas.</td></tr>
            ) : facturasPaginadas.map((f) => (
              <tr key={f.id}>
                <td className="text-sm" style={{ whiteSpace: 'nowrap' }}>{formatFechaEmision(f.fecha_emision)}</td>
                <td>
                  <div className="mono">{numero(f)}</div>
                  <span
                    className="mono text-xs text-secondary"
                    title={`${f.clave_acceso} — clic para copiar`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      navigator.clipboard.writeText(f.clave_acceso);
                      toast.success('Clave de acceso copiada');
                    }}
                  >
                    {truncarClave(f.clave_acceso)}
                  </span>
                </td>
                <td>
                  <span style={{
                    display: 'inline-block', padding: '1px 7px', borderRadius: '10px',
                    fontSize: '10.5px', fontWeight: 600,
                    background: f.tipo === 'servicios' ? '#e3f2fd' : '#fff3e0',
                    color: f.tipo === 'servicios' ? '#1565c0' : '#e65100',
                  }}>
                    {TIPO_LABEL[f.tipo] || f.tipo || '—'}
                  </span>
                </td>
                <td>
                  <div>{f.comprador_nombre || f.cliente_nombre}</div>
                  <div className="text-xs text-secondary">
                    {f.comprador_identificacion || '—'} · {f.cliente_casillero}
                  </div>
                </td>
                <td className="num mono">{dinero(f.subtotal)}</td>
                <td className="num mono">{dinero(f.iva)}</td>
                <td className="num mono" style={{ fontWeight: 600 }}>{dinero(f.total)}</td>
                <td><EstadoFacturaBadge estado={f.estado} /></td>
                <td>
                  {f.errores && f.errores.length > 0 && (
                    <button
                      className="btn-ghost btn-sm"
                      onClick={() => setErroresVisible(erroresVisible === f.id ? null : f.id)}
                      title="Ver errores"
                      style={{ color: 'var(--color-danger, #c62828)' }}
                    >
                      <AlertCircle size={13} /> {f.errores.length}
                    </button>
                  )}
                </td>
                <td className="text-right">
                  <div style={{ display: 'flex', gap: 'var(--spacing-1)', justifyContent: 'flex-end', whiteSpace: 'nowrap' }}>
                    {puedeReenviar(f.estado) && (
                      <button
                        className="btn-ghost btn-sm"
                        onClick={() => reenviar(f)}
                        disabled={reenviando === f.id}
                        title="Reenvía el MISMO comprobante — úsalo solo si el SRI estuvo caído/lento, no si fue un error de datos"
                      >
                        {reenviando === f.id ? (
                          <RefreshCw size={13} className="spin" />
                        ) : (
                          <RotateCcw size={13} />
                        )}
                        {' '}Reintentar
                      </button>
                    )}
                    {f.estado === 'rechazada' && (
                      <button
                        className="btn-ghost btn-sm"
                        onClick={() => volverAEmitir(f)}
                        disabled={reemitiendo === f.id}
                        title="Genera un comprobante NUEVO con los datos actuales del cliente — úsalo si corregiste un dato (dirección, nombre, etc.)"
                        style={{ color: 'var(--color-primary)' }}
                      >
                        {reemitiendo === f.id ? (
                          <RefreshCw size={13} className="spin" />
                        ) : (
                          <FileEdit size={13} />
                        )}
                        {' '}Volver a emitir
                      </button>
                    )}
                    {f.estado === 'autorizada' && (
                      <>
                        <button
                          className="btn-ghost btn-sm"
                          onClick={() => descargarPdf(f)}
                          title="Descargar PDF (RIDE) para enviar al cliente"
                        >
                          <FileText size={13} /> PDF
                        </button>
                        <button
                          className="btn-ghost btn-sm"
                          onClick={() => descargarXml(f)}
                          title="Descargar XML autorizado por el SRI"
                        >
                          <Download size={13} /> XML
                        </button>
                        <button
                          className="btn-ghost btn-sm"
                          onClick={() => enviarAlCliente(f)}
                          disabled={enviandoCorreo === f.id}
                          title={f.correo_enviado_en
                            ? `Enviada a ${f.correo_enviado_a} el ${new Date(f.correo_enviado_en).toLocaleDateString('es-EC')} — clic para reenviar`
                            : 'Enviar PDF y XML al cliente por correo'}
                          style={f.correo_enviado_en ? { color: 'var(--color-success)' } : undefined}
                        >
                          {enviandoCorreo === f.id ? <RefreshCw size={13} className="spin" />
                            : f.correo_enviado_en ? <MailCheck size={13} /> : <Mail size={13} />}
                          {' '}{f.correo_enviado_en ? 'Enviada' : 'Enviar'}
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {erroresVisible && (() => {
        const f = facturas.find((x) => x.id === erroresVisible);
        if (!f) return null;
        const info = ESTADO_DESCRIPCION[f.estado] || {};
        return (
          <div style={{
            margin: 'var(--spacing-3) 0', padding: 'var(--spacing-3)',
            background: '#fff8f0', borderRadius: '8px', border: '1px solid #ffe0b2',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-2)' }}>
              <strong>
                Factura {numero(f)}
              </strong>
              <button className="btn-ghost btn-sm" onClick={() => setErroresVisible(null)}>Cerrar</button>
            </div>

            <div style={{ marginBottom: 'var(--spacing-3)', padding: 'var(--spacing-2)', background: 'var(--color-bg-secondary)', borderRadius: '6px' }}>
              <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                Estado: {info.titulo || f.estado}
              </div>
              {info.instruccion && (
                <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                  {info.instruccion}
                </div>
              )}
            </div>

            {f.errores && f.errores.length > 0 && (
              <>
                <div style={{ fontWeight: 600, marginBottom: '4px', color: '#c62828' }}>
                  Detalle del error:
                </div>
                {f.errores.map((err, i) => (
                  <div key={i} style={{ fontSize: '0.85rem', marginBottom: '8px', padding: 'var(--spacing-2)', background: '#fff3f3', borderRadius: '4px' }}>
                    <div>{interpretarError(err.mensaje)}</div>
                    {err.informacionAdicional && (
                      <div className="text-xs text-secondary" style={{ marginTop: '4px' }}>
                        Info adicional: {err.informacionAdicional}
                      </div>
                    )}
                    <div className="text-xs text-secondary" style={{ marginTop: '4px', fontFamily: 'monospace' }}>
                      Código: {err.identificador || '—'} · Tipo: {err.tipo || '—'}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        );
      })()}

      {facturas.length > 0 && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginTop: 'var(--spacing-4)', flexWrap: 'wrap', gap: 'var(--spacing-3)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <span className="text-sm text-secondary">Mostrar</span>
            <select
              value={porPagina}
              onChange={(e) => setPorPagina(Number(e.target.value))}
              style={{ width: 'auto', minWidth: '70px' }}
            >
              {[10, 20, 30, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <span className="text-sm text-secondary">por página</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <span className="text-sm text-secondary">
              {(paginaActual - 1) * porPagina + 1}–{Math.min(paginaActual * porPagina, facturas.length)} de {facturas.length}
            </span>
            <button className="btn-ghost btn-sm" disabled={paginaActual <= 1} onClick={() => setPagina(paginaActual - 1)}>
              <ChevronLeft size={16} />
            </button>
            <button className="btn-ghost btn-sm" disabled={paginaActual >= totalPaginas} onClick={() => setPagina(paginaActual + 1)}>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
